import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Divider, Drawer, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Close, ExpandMore, Menu as MenuIcon } from '@mui/icons-material';
import { useFeatures } from '../contexts/FeaturesContext';

/**
 * Shared public site header (logged-out / public pages AND the landing page).
 *
 * Teal brand gradient (src/theme/colors.js brand.gradient) with white logo +
 * links and a white "Get Started" CTA — one consistent header everywhere so
 * the homepage, login, and the Verify-Work funnel all match.
 */
const FONT = "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
const DEEP_TEAL = '#185451';
const TEAL_DARK = '#134e4a';
const GRADIENT = `linear-gradient(135deg, ${DEEP_TEAL} 0%, ${TEAL_DARK} 100%)`;

const LINKS = [
  { label: 'About', to: '/about' },
  { label: 'Trust & Safety', to: '/trust' },
  // Only on deployments running the property-listings vertical.
  { label: 'Property', feature: 'listings', menu: [
    { label: 'Homes for sale', hint: 'Verified houses and stands, with a map', to: '/properties' },
    { label: 'How buying works', hint: 'What we check, and each step to the title', to: '/buying-property' },
    { label: 'Find a realtor', hint: 'Members in good standing with the EAC', to: '/realtors' },
    { label: 'Find a conveyancer', hint: 'Members in good standing with the Law Society', to: '/conveyancers' },
    { label: 'Property check', hint: 'A verified person visits and sends dated photos', to: '/property-check' },
  ] },
  { label: 'How it works', to: '/', scrollId: 'how-it-works' },
  { label: 'Contractors', to: '/for-contractors' },
  { label: 'Verifiers', to: '/for-verifiers' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
];

export default function PublicHeader({ showLinks = true, showAuthButtons = true, rightSlot = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const features = useFeatures();
  const links = LINKS.filter((l) => !l.feature || features[l.feature]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  // Below the desktop breakpoint the links do not fit the bar: they (and Log
  // in) move into a panel behind a menu button, so phones get every link too.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const go = (l) => {
    setDrawerOpen(false);
    if (l.scrollId && location.pathname === '/') {
      document.getElementById(l.scrollId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(l.to);
    }
  };

  const handleLink = (l) => (e) => {
    if (l.menu) { setMenuItems(l.menu); setMenuAnchor(e.currentTarget); return; }
    // On the homepage, "How it works" smooth-scrolls to its section; elsewhere navigate.
    if (l.scrollId && location.pathname === '/') {
      document.getElementById(l.scrollId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(l.to);
    }
  };

  return (
    <Box
      component="nav"
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 'clamp(1.25rem,4vw,3rem)', height: 64,
        background: GRADIENT, color: '#fff',
        position: 'sticky', top: 0, zIndex: 1100,
      }}
    >
      <Box
        component="img"
        src="/images/logo-eqaya-mark-light.png"
        alt="Eqaya Inc."
        onClick={() => navigate('/')}
        sx={{ height: 34, width: 'auto', display: 'block', cursor: 'pointer' }}
      />

      {showLinks && (
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: '1.5rem', alignItems: 'center' }}>
          {links.map((l) => (
            <Box
              key={l.label}
              component="span"
              onClick={handleLink(l)}
              sx={{
                fontFamily: FONT, fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer', '&:hover': { color: '#fff' },
              }}
            >
              {l.label}
              {l.menu && <ExpandMore sx={{ fontSize: 18, ml: 0.25, verticalAlign: 'middle' }} />}
            </Box>
          ))}
          <Menu
            anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { mt: 1.5, borderRadius: 2, minWidth: 340, p: 0.5 } }}
          >
            {menuItems.map((m) => (
              <MenuItem key={m.to} onClick={() => { setMenuAnchor(null); navigate(m.to); }}
                selected={location.pathname === m.to} sx={{ borderRadius: 1.5, py: 1.25, display: 'block' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#1e293b' }}>{m.label}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>{m.hint}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {rightSlot}
        {showAuthButtons && (
          <>
            <Box
              component="button"
              onClick={() => navigate('/login')}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center',
                bgcolor: 'transparent', border: '1.5px solid rgba(255,255,255,0.6)', color: '#fff',
                px: '1.4rem', py: '0.55rem', borderRadius: '9px',
                fontFamily: FONT, fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Log in
            </Box>
            <Box
              component="button"
              onClick={() => navigate('/register')}
              sx={{
                display: 'inline-flex', alignItems: 'center',
                bgcolor: '#fff', border: 'none', color: DEEP_TEAL,
                px: '1.4rem', py: '0.6rem', borderRadius: '9px',
                fontFamily: FONT, fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Get Started
            </Box>
          </>
        )}
        {(showLinks || showAuthButtons) && (
          <IconButton aria-label="Open menu" onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#fff', ml: -0.5 }}>
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 'min(86vw, 360px)', fontFamily: FONT } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, height: 64, background: GRADIENT }}>
          <Box component="img" src="/images/logo-eqaya-mark-light.png" alt="Eqaya Inc." sx={{ height: 30 }} />
          <IconButton aria-label="Close menu" onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>
        <Box component="nav" aria-label="Site" sx={{ py: 1 }}>
          {showLinks && links.map((l) => (l.menu ? (
            <Box key={l.label} sx={{ px: 2.5, pt: 1.5, pb: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569', mb: 0.5 }}>{l.label}</Typography>
              {l.menu.map((m) => (
                <Box key={m.to} component="button" onClick={() => go(m)}
                  sx={{ display: 'block', width: '100%', textAlign: 'left', border: 0, bgcolor: location.pathname === m.to ? '#e6f4f2' : 'transparent', borderRadius: '8px', px: 1.25, py: 1, cursor: 'pointer', fontFamily: FONT }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{m.label}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#475569' }}>{m.hint}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box key={l.label} component="button" onClick={() => go(l)}
              sx={{ display: 'block', width: '100%', textAlign: 'left', border: 0, bgcolor: location.pathname === l.to && !l.scrollId ? '#e6f4f2' : 'transparent', px: 2.5, py: 1.5, cursor: 'pointer', fontFamily: FONT, fontSize: 17, fontWeight: 600, color: '#1e293b' }}>
              {l.label}
            </Box>
          )))}
        </Box>
        {showAuthButtons && (
          <>
            <Divider />
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box component="button" onClick={() => { setDrawerOpen(false); navigate('/register'); }}
                sx={{ height: 48, border: 0, borderRadius: '10px', bgcolor: DEEP_TEAL, color: '#fff', fontFamily: FONT, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                Get Started
              </Box>
              <Box component="button" onClick={() => { setDrawerOpen(false); navigate('/login'); }}
                sx={{ height: 48, border: `1.5px solid ${DEEP_TEAL}`, borderRadius: '10px', bgcolor: '#fff', color: DEEP_TEAL, fontFamily: FONT, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                Log in
              </Box>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
}
